import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { acceptProjectCollaboration, rejectProjectCollaboration } from '@services/projects.service';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { PROJECT_COLLABORATION_QUERY_KEY } from './useProjectCollaboration';
import { NOTIFICATIONS_QUERY_KEY } from '@hooks/useNotifications';

/**
 * Accept/reject collaboration invite from notification (metadata.projectId, metadata.collaborationId).
 * Invalidates notifications and project collaboration list.
 */
export function useCollaborationInviteAction() {
    const queryClient = useQueryClient();

    const acceptMutation = useMutation({
        mutationFn: ({ projectId, collaborationId }) =>
            acceptProjectCollaboration(projectId, collaborationId ? { collaborationId } : {}),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: PROJECT_COLLABORATION_QUERY_KEY(projectId) });
            invalidateProjectQueries(queryClient, projectId);
            message.success('Collaboration invite accepted.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to accept invite.');
        },
    });

    const rejectMutation = useMutation({
        mutationFn: ({ projectId, collaborationId }) =>
            rejectProjectCollaboration(projectId, collaborationId ? { collaborationId } : {}),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: PROJECT_COLLABORATION_QUERY_KEY(projectId) });
            invalidateProjectQueries(queryClient, projectId);
            message.success('Collaboration invite rejected.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to reject invite.');
        },
    });

    return {
        accept: acceptMutation.mutate,
        reject: rejectMutation.mutate,
        acceptPending: acceptMutation.isPending,
        rejectPending: rejectMutation.isPending,
    };
}
